import { useDraggable } from '@dnd-kit/core'
import { useState } from 'react'
import { collectSubtreeIds, type DependentComponent, findDependentComponents } from '../helpers/conditionalHelpers'
import { useFormBuilderStore } from '../store'
import type { FormInputComponent } from '../types'

export const useFormComponentWrapper = (component: FormInputComponent, index: number, containerId?: string) => {
	const selectComponent = useFormBuilderStore((state) => state.selectComponent)
	const selectedComponentId = useFormBuilderStore((state) => state.selectedComponentId)
	const deleteComponent = useFormBuilderStore((state) => state.deleteComponent)
	const duplicateComponent = useFormBuilderStore((state) => state.duplicateComponent)
	const formComponents = useFormBuilderStore((state) => state.formComponents)
	const cleanOrphanedConditions = useFormBuilderStore((state) => state.cleanOrphanedConditions)

	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [dependentComponents, setDependentComponents] = useState<DependentComponent[]>([])
	const [pendingSubtreeIds, setPendingSubtreeIds] = useState<string[]>([])

	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: `component-${component.id}`,
		data: {
			id: component.id,
			name: component.name,
			containerId: containerId,
			sourceContainerId: containerId,
			sourceIndex: index,
			column: (component.custom as Record<string, unknown> | null)?.column as number | undefined,
		},
	})

	const isSelected = selectedComponentId === component.id

	const handleSelectComponent = (e: React.MouseEvent) => {
		e.stopPropagation()
		selectComponent(component.id)
	}

	const handleDelete = (e: React.MouseEvent) => {
		e.stopPropagation()

		const subtreeIds = collectSubtreeIds(component)
		const excludeIds = new Set(subtreeIds)

		const allDependents: DependentComponent[] = []
		const seenIds = new Set<string>()
		for (const id of subtreeIds) {
			for (const dep of findDependentComponents(formComponents, id, excludeIds)) {
				if (!seenIds.has(dep.id)) {
					seenIds.add(dep.id)
					allDependents.push(dep)
				}
			}
		}

		if (allDependents.length > 0) {
			setDependentComponents(allDependents)
			setPendingSubtreeIds(subtreeIds)
			setIsDeleteDialogOpen(true)
		} else {
			deleteComponent(component.id)
		}
	}

	const handleConfirmDelete = () => {
		// Clean all orphaned conditions in a single store update, then delete
		cleanOrphanedConditions(pendingSubtreeIds)
		deleteComponent(component.id)
		setIsDeleteDialogOpen(false)
		setDependentComponents([])
		setPendingSubtreeIds([])
	}

	const handleCancelDelete = () => {
		setIsDeleteDialogOpen(false)
		setDependentComponents([])
		setPendingSubtreeIds([])
	}

	const handleDuplicate = (e: React.MouseEvent) => {
		e.stopPropagation()
		duplicateComponent(component.id)
	}

	return {
		handleSelectComponent,
		handleDelete,
		handleDuplicate,
		handleConfirmDelete,
		handleCancelDelete,
		isSelected,
		isDragging,
		isDeleteDialogOpen,
		dependentComponents,
		componentLabel: component.label || component.name,
		attributes,
		listeners,
		setNodeRef,
	}
}
